import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { ArrowRightSmall, ChatFill, ChevronDownSmall, LikeFill } from '@peacecover/icon';
import { t } from 'i18next';
import { useGetSceneSubscriptions } from '../../../domains/scene/ui/hooks/useGetSceneSubscriptions';
import EmptyCase from '../../../components/errors/EmptyCase';
import PreviewHeaderItem from './PreviewHeaderItem';
import ThreadContentItem from './ThreadContentItem';
import ChatContentItem from './ChatContentItem';
import PreviewCastRelationship from './PreviewCastRelationship';
import { MenuDropdown, MenuDropdownItem } from '../../components/popup/MenuDropdown';
import { ReactComponent as ThreadIcon } from '../../../icons/thread.svg';
import { ROUTES } from '../../../constants/routes';
import type { components } from '../../../types/schema';

type SceneSubscription = components['schemas']['ApiV1UserSceneSubscription'];
type ThreadLastReadContent = components['schemas']['ApiV1AdaptedFictionThreadContent'];
type ChatLastReadContent = components['schemas']['ApiV1AdaptedFictionChatContent'];

type FilterType = 'all' | 'recent' | 'liked';

const FILTER_LABELS: Record<FilterType, string> = {
  all: t('SceneMain.subscription.filter.all'),
  recent: t('SceneMain.subscription.filter.recent'),
  liked: t('SceneMain.subscription.filter.liked')
};

const FILTER_PARAM = 'filter';

const PreviewSubscriptionList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = useMemo(() => {
    const param = searchParams.get(FILTER_PARAM);
    return param === 'recent' || param === 'liked' ? param : 'all';
  }, [searchParams]);
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useGetSceneSubscriptions([
    'SceneThread',
    'SceneChat'
  ]);
  const previewList = data?.pages?.flatMap((page) => page.userSceneSubscriptions);
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);

  const filteredList =
    filter === 'liked'
      ? (previewList?.filter((subscription) => subscription?.isLiked) ?? [])
      : (previewList ?? []);

  const loadMore = () => {
    if (isFetching || !hasNextPage) return;
    fetchNextPage();
  };

  if (isLoading) {
    return null;
  }

  if (data?.pages?.length && previewList?.length === 0) {
    return (
      // @ts-ignore
      <EmptyCase emoji="😳" title={t('SceneMain.subscription.emptyTitle')} />
    );
  }

  const handleFilterSelect = (newFilter: FilterType) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (newFilter === 'all') {
          next.delete(FILTER_PARAM);
        } else {
          next.set(FILTER_PARAM, newFilter);
        }
        return next;
      },
      { replace: true }
    );
    setIsMenuDropdownOpen(false);
  };

  return (
    <div className="h-full">
      <Virtuoso
        overscan={480}
        defaultItemHeight={260}
        style={{ height: '100%' }}
        data={filteredList}
        endReached={loadMore}
        atBottomThreshold={200}
        components={{
          Header: () => (
            <div className="flex items-center justify-end gap-[5px] mt-[20px] mb-[10px] px-[24px]">
              <p
                className="text-[15px] font-semibold leading-[18px] text-pure-white cursor-pointer"
                onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
              >
                {FILTER_LABELS[filter]}
              </p>
              <ChevronDownSmall
                size={18}
                className="text-pure-white"
                onClick={() => setIsMenuDropdownOpen(!isMenuDropdownOpen)}
              />
              {isMenuDropdownOpen && (
                <MenuDropdown
                  className="absolute top-[45px] right-[13px] z-10"
                  onClickOutside={() => setIsMenuDropdownOpen(false)}
                >
                  <MenuDropdownItem
                    isActive={filter === 'all'}
                    onClick={() => handleFilterSelect('all')}
                  >
                    {t('SceneMain.subscription.filter.all')}
                  </MenuDropdownItem>
                  <MenuDropdownItem
                    isActive={filter === 'recent'}
                    onClick={() => handleFilterSelect('recent')}
                  >
                    {t('SceneMain.subscription.filter.recent')}
                  </MenuDropdownItem>
                  <MenuDropdownItem
                    isActive={filter === 'liked'}
                    onClick={() => handleFilterSelect('liked')}
                  >
                    {t('SceneMain.subscription.filter.liked')}
                  </MenuDropdownItem>
                </MenuDropdown>
              )}
            </div>
          )
        }}
        itemContent={(_index, subscription: SceneSubscription) => (
          <PreviewSubscriptionItem subscription={subscription} />
        )}
      />
    </div>
  );
};

export default PreviewSubscriptionList;

const PreviewSubscriptionItem = ({ subscription }: { subscription: SceneSubscription }) => {
  const navigate = useNavigate();
  const tags = [...(subscription?.keywords ?? []), ...(subscription?.worldview ?? [])];

  const getChatDirection = (speaker: string) => {
    if (!speaker) return 'left';
    if (speaker === subscription?.castRelationship?.personAName) return 'left';
    if (speaker === subscription?.castRelationship?.personBName) return 'right';
    return 'left';
  };

  return (
    <div className="pt-[20px]">
      <div className="px-[22px] flex items-center justify-between">
        <PreviewCastRelationship
          castRelationship={subscription?.castRelationship ?? null}
          className="text-[#FFF]"
        />
        <div className="flex items-center gap-[4px]">
          {subscription?.contentType === 'SceneThread' ? (
            <ThreadIcon width={14} height={14} />
          ) : (
            <ChatFill size={14} />
          )}
          <p className="text-[12px] font-medium leading-[14px] text-pure-white">
            {subscription?.contentType === 'SceneThread'
              ? t('SceneMain.subscription.thread')
              : t('SceneMain.subscription.chat')}{' '}
            {subscription?.currentSeq}/{subscription?.count}
          </p>
        </div>
      </div>
      <PreviewHeaderItem
        tags={tags}
        summary={subscription?.summary ?? ''}
        className="px-[22px] mt-[16px]"
      />
      {subscription?.contentType === 'SceneThread' ? (
        <ThreadContentItem
          speaker={(subscription?.lastReadContent as ThreadLastReadContent)?.speaker ?? undefined}
          text={(subscription?.lastReadContent as ThreadLastReadContent)?.text ?? ''}
          className="mt-[8px]"
          hasLine={false}
        />
      ) : (
        <ChatContentItem
          chatType={(subscription?.lastReadContent as ChatLastReadContent)?.chatType}
          text={(subscription?.lastReadContent as ChatLastReadContent)?.text ?? ''}
          pictureUrl={(subscription?.lastReadContent as ChatLastReadContent)?.pictureUrl ?? ''}
          direction={getChatDirection(
            (subscription?.lastReadContent as ChatLastReadContent)?.speaker ?? ''
          )}
          className={`mt-[17px] ${(subscription?.lastReadContent as ChatLastReadContent)?.chatType === 'dialogue' ? '!mx-[22px]' : ''}`}
        />
      )}
      <div className="border-t-[1px] border-b-[5px] border-[#171717] flex items-center justify-between px-[22px] py-[14px]">
        <div className="flex items-center gap-[12px]">
          <div className="flex items-center gap-[4px]">
            <LikeFill
              size={18}
              className="text-pure-white"
              gradientStops={
                subscription?.isLiked
                  ? [
                      { offset: '0%', color: '#FF45A9' },
                      { offset: '100%', color: '#FF6A6A' }
                    ]
                  : []
              }
            />
            <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
              {subscription?.likesCount}
            </p>
          </div>
          <div className="flex items-center gap-[4px] text-pure-white">
            <ChatFill size={18} />
            <p className="text-[11px] font-semibold leading-[13px]">
              {subscription?.commentsCount}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="text-pure-white text-[12px] font-medium leading-[14px] flex items-center gap-[4px]"
          onClick={() =>
            navigate(
              `${ROUTES.SCENE.PREVIEW_DETAIL.GO(subscription.sceneId ?? '')}?cursor=${subscription.contentId}`
            )
          }
        >
          {t('SceneMain.subscription.continue')}
          <ArrowRightSmall size={14} />
        </button>
      </div>
    </div>
  );
};
